/**  DO NOT SIMPLIFY OR REWRITE THIS FILE.
 *   This file provides educational control content and is enforced by tests.
 *   If you need to change it, update tests in src/ui-contract/__tests__/ and keep the shape.
 */

// src/lib/hoverDetails.ts
export type ControlDetail = {
  title: string;
  summary: string;
  why: string[];
  goodLooksLike: string[];
  evidence: string[];
};

export const CONTROL_DETAILS: Record<string, ControlDetail> = {
  // Governance Controls
  "GV.OC": {
    title: "Organizational Context & Cybersecurity",
    summary: "Define how cybersecurity supports the organization's mission, objectives, and stakeholder expectations.",
    why: [
      "Aligns security strategy with business priorities.",
      "Ensures security investments support organizational goals.",
      "Clarifies security's role in achieving mission success."
    ],
    goodLooksLike: [
      "Security strategy linked to business objectives.",
      "Documented understanding of critical business processes.",
      "Stakeholder requirements incorporated into security planning."
    ],
    evidence: [
      "Security strategy document aligned to business goals.",
      "Business impact analysis for critical processes.",
      "Stakeholder consultation records."
    ]
  },
  "GV.CO": {
    title: "Cybersecurity Supply Chain Obligations",
    summary: "Establish and communicate cybersecurity requirements and obligations for suppliers and partners.",
    why: [
      "Ensures consistent security across the supply chain.",
      "Clarifies expectations for third-party security controls.",
      "Reduces risk from supplier vulnerabilities."
    ],
    goodLooksLike: [
      "Standard security requirements in all supplier contracts.",
      "Risk-based security obligations by supplier tier.",
      "Regular communication of updated security requirements."
    ],
    evidence: [
      "Supplier security requirements documentation.",
      "Contract templates with security clauses.",
      "Supplier communication and acknowledgment records."
    ]
  },
  "GV.OV": {
    title: "Cybersecurity Oversight",
    summary: "Establish oversight and accountability for organizational cybersecurity activities and outcomes.",
    why: [
      "Ensures senior leadership engagement in security.",
      "Provides clear accountability for security outcomes.",
      "Enables informed decision-making on security matters."
    ],
    goodLooksLike: [
      "Board or executive committee reviews security regularly.",
      "Clear security accountability assigned to executives.",
      "Security metrics reported to leadership regularly."
    ],
    evidence: [
      "Board or executive meeting minutes covering security.",
      "Executive security accountability documentation.",
      "Regular security reporting to leadership."
    ]
  },
  "GV.RM": {
    title: "Risk Management Strategy",
    summary: "Establish and execute a comprehensive cybersecurity risk management strategy.",
    why: [
      "Provides systematic approach to identifying and managing risks.",
      "Enables risk-informed decision making.",
      "Supports prioritization of security investments."
    ],
    goodLooksLike: [
      "Documented risk management strategy and processes.",
      "Regular risk assessments across all critical systems.",
      "Risk register actively maintained and reviewed."
    ],
    evidence: [
      "Risk management strategy documentation.",
      "Risk assessment reports.",
      "Risk register with treatment plans."
    ]
  },
  "GV.PO": {
    title: "Policy",
    summary: "Establish, communicate, and maintain organizational cybersecurity policy.",
    why: [
      "Sets baseline expectations for security across the organization.",
      "Provides authority for security requirements.",
      "Demonstrates commitment to security to stakeholders."
    ],
    goodLooksLike: [
      "Comprehensive cybersecurity policy approved by leadership.",
      "Policy communicated and accessible to all staff.",
      "Regular policy reviews and updates."
    ],
    evidence: [
      "Approved cybersecurity policy documents.",
      "Policy communication and training records.",
      "Policy review and approval history."
    ]
  },
  "GV.RR": {
    title: "Roles, Responsibilities & Authorities",
    summary: "Establish and communicate cybersecurity roles, responsibilities, and authorities.",
    why: [
      "Clarifies who is responsible for security activities.",
      "Prevents gaps in security coverage.",
      "Enables accountability for security outcomes."
    ],
    goodLooksLike: [
      "RACI matrix defining security roles and responsibilities.",
      "Clear authority levels for security decisions.",
      "Security responsibilities in job descriptions."
    ],
    evidence: [
      "Roles and responsibilities documentation.",
      "RACI matrix or similar.",
      "Job descriptions with security responsibilities."
    ]
  },
  
  // Governance / Risk Management (NIST-style codes)
  "GV.RM-01": {
    title: "Risk Management Framework",
    summary: "Establish a formal risk management framework with thresholds, assumptions, and stakeholder approvals.",
    why: [
      "Provides consistent approach to risk decisions across the organization.",
      "Clarifies risk appetite and acceptable thresholds.",
      "Enables stakeholder alignment on critical risk decisions."
    ],
    goodLooksLike: [
      "Documented framework with risk tiers and thresholds.",
      "Executive approval of risk appetite and tolerances.",
      "Regular risk committee meetings with action tracking.",
      "Enterprise Risk Register tracking identified cyber risks."
    ],
    evidence: [
      "Risk management framework policy.",
      "Board/executive approval documentation.",
      "Risk committee meeting minutes and decisions."
    ]
  },
  "GV.RM-02": {
    title: "Enterprise Risk Register",
    summary: "Maintain a centralized register documenting identified cybersecurity risks and their management.",
    why: [
      "Provides comprehensive view of organizational risk exposure.",
      "Enables tracking of risk treatment and mitigation progress.",
      "Supports informed decision-making and resource allocation."
    ],
    goodLooksLike: [
      "Current risk register with all identified cyber risks.",
      "Risk owners and treatment plans assigned.",
      "Regular reviews and updates of risk status."
    ],
    evidence: [
      "Risk register or risk database export.",
      "Risk treatment plans and tracking.",
      "Risk review meeting minutes."
    ]
  },
  "GV.RM-03": {
    title: "Risk Assessment Process",
    summary: "Systematic process for identifying, analyzing, and evaluating cybersecurity risks.",
    why: [
      "Ensures consistent risk evaluation across the organization.",
      "Identifies risks before they can be exploited.",
      "Supports prioritization of security investments."
    ],
    goodLooksLike: [
      "Documented risk assessment methodology.",
      "Regular risk assessments for all critical systems.",
      "Risk criteria and scoring consistently applied."
    ],
    evidence: [
      "Risk assessment methodology documentation.",
      "Completed risk assessment reports.",
      "Risk scoring criteria and guidelines."
    ]
  },
  
  // Governance / Supply Chain
  "ID.GV-1": {
    title: "Organizational Cybersecurity Policy",
    summary: "Establish, document, and communicate organizational cybersecurity policy with clear roles and responsibilities.",
    why: [
      "Sets expectations for security across all teams.",
      "Clarifies accountability and decision authority.",
      "Provides foundation for consistent security practices."
    ],
    goodLooksLike: [
      "Board-approved cybersecurity policy.",
      "RACI matrix showing security responsibilities.",
      "Evidence of policy communication and acknowledgment."
    ],
    evidence: [
      "Approved cybersecurity policy document.",
      "Roles and responsibilities matrix.",
      "Training records and acknowledgment receipts."
    ]
  },
  "ID.GV-3": {
    title: "Regulatory Compliance & Legal Requirements",
    summary: "Identify, document, and manage applicable cybersecurity and privacy regulations.",
    why: [
      "Ensures awareness of legal obligations.",
      "Reduces regulatory and legal risks.",
      "Guides control implementation priorities."
    ],
    goodLooksLike: [
      "Compliance register with all applicable regulations.",
      "Gap assessments and remediation plans.",
      "Regular regulatory updates and impact assessments."
    ],
    evidence: [
      "Compliance register or matrix.",
      "Audit reports and certifications.",
      "Gap analysis and remediation tracking."
    ]
  },
  "ID.SC-3": {
    title: "Supplier Contracts & Security Requirements",
    summary: "Use contracts with suppliers and third parties to enforce cybersecurity requirements and controls.",
    why: [
      "Ensures suppliers meet minimum security standards.",
      "Provides legal recourse for security failures.",
      "Clarifies security expectations and obligations."
    ],
    goodLooksLike: [
      "Standard security clauses in all supplier contracts.",
      "Risk-tiered requirements based on supplier access.",
      "Audit rights and breach notification requirements."
    ],
    evidence: [
      "Sample contracts with security clauses.",
      "Security addendum templates.",
      "Contract compliance verification records."
    ]
  },
  "ID.SC-5": {
    title: "Supplier Oversight & Assessments",
    summary: "Assess suppliers and third parties through audits, evaluations, or compliance reviews.",
    why: [
      "Validates supplier security claims and controls.",
      "Identifies and remediates supplier risks.",
      "Maintains ongoing visibility into supplier security posture."
    ],
    goodLooksLike: [
      "Risk-based supplier assessment schedule.",
      "Third-party audit reports and evaluations.",
      "Remediation tracking for identified gaps."
    ],
    evidence: [
      "Supplier audit reports.",
      "Assessment criteria and checklists.",
      "Remediation plans and closure documentation."
    ]
  },
  "ID.RA-1": {
    title: "Vulnerability Identification & Assessment",
    summary: "Identify and document cybersecurity vulnerabilities through assessments and testing.",
    why: [
      "Discovers security weaknesses before attackers do.",
      "Enables prioritized remediation efforts.",
      "Supports continuous security improvement."
    ],
    goodLooksLike: [
      "Regular vulnerability scanning across all systems.",
      "Penetration testing for critical systems.",
      "Documented assessment findings and risk ratings."
    ],
    evidence: [
      "Vulnerability scan reports.",
      "Penetration test results.",
      "Risk assessment documentation."
    ]
  },
  "ID.RA-01": {
    title: "Vulnerability Identification & Assessment",
    summary: "Identify and document cybersecurity vulnerabilities through assessments and testing.",
    why: [
      "Discovers security weaknesses before attackers do.",
      "Enables prioritized remediation efforts.",
      "Supports continuous security improvement."
    ],
    goodLooksLike: [
      "Regular vulnerability scanning across all systems.",
      "Penetration testing for critical systems.",
      "Documented assessment findings and risk ratings."
    ],
    evidence: [
      "Vulnerability scan reports.",
      "Penetration test results.",
      "Risk assessment documentation."
    ]
  },
  "ID.RA-06": {
    title: "Vulnerability Assessment",
    summary:
      "Conduct recurring vulnerability assessments to evaluate security weaknesses, validate exposure, and inform remediation priorities.",
    why: [
      "Validates the depth and accuracy of identified vulnerabilities.",
      "Prioritizes remediation based on risk and business impact.",
      "Informs ongoing risk management decisions."
    ],
    goodLooksLike: [
      "Recurring scheduled assessments across internal and external assets.",
      "Findings prioritized by severity, exploitability, and business impact.",
      "Remediation progress tracked and reassessed."
    ],
    evidence: [
      "Vulnerability assessment and penetration testing reports.",
      "External attack surface review reports.",
      "Risk-rating methodology and reassessment / validation testing results."
    ]
  },
  "ID.RA-5": {
    title: "Threat Intelligence & Analysis",
    summary: "Use threat intelligence to understand threats, vulnerabilities, likelihood, and impact.",
    why: [
      "Focuses defenses on real and relevant threats.",
      "Improves detection and response capabilities.",
      "Supports risk-informed decision making."
    ],
    goodLooksLike: [
      "Threat intelligence feeds integrated into security tools.",
      "Regular threat briefings for security teams.",
      "Threat-informed detection and response strategies."
    ],
    evidence: [
      "Threat intelligence reports.",
      "Intelligence-driven detection rules.",
      "Threat briefing documentation."
    ]
  },
  "ID.RM-1": {
    title: "Risk Identification & Documentation",
    summary: "Identify, document, and communicate cybersecurity risks throughout the organization.",
    why: [
      "Provides visibility into organizational risk exposure.",
      "Enables informed risk acceptance decisions.",
      "Supports resource allocation for risk treatment."
    ],
    goodLooksLike: [
      "Comprehensive risk register with current risks.",
      "Risk owners assigned for each identified risk.",
      "Regular risk reporting to leadership."
    ],
    evidence: [
      "Risk register or database.",
      "Risk assessment reports.",
      "Risk communication records to stakeholders."
    ]
  },
  "ID.AM-6": {
    title: "Cybersecurity Roles Within Operations",
    summary: "Define and communicate cybersecurity roles and responsibilities within operations and engineering teams.",
    why: [
      "Clarifies security ownership in operational contexts.",
      "Ensures security is integrated into daily operations.",
      "Reduces gaps between security and operations teams."
    ],
    goodLooksLike: [
      "Security responsibilities embedded in job descriptions.",
      "Clear escalation paths for security incidents.",
      "Regular security training for operational staff."
    ],
    evidence: [
      "Job descriptions with security responsibilities.",
      "Organizational charts showing security roles.",
      "Training records for operational teams."
    ]
  },
  "ID.AM-3": {
    title: "Communication & Data Flow Mapping",
    summary: "Map organizational communication paths and data flows to understand dependencies and risks.",
    why: [
      "Identifies critical data paths and dependencies.",
      "Supports network segmentation decisions.",
      "Enables targeted monitoring and protection."
    ],
    goodLooksLike: [
      "Current data flow diagrams for key systems.",
      "Network architecture documentation.",
      "Regular updates as systems change."
    ],
    evidence: [
      "Data flow diagrams.",
      "Network architecture documentation.",
      "Change records for flow updates."
    ]
  },
  "ID.AM-4": {
    title: "External Information System Connections",
    summary: "Identify and catalog external connections and data exchanges with partners, customers, and suppliers.",
    why: [
      "Increases visibility into external attack surface.",
      "Supports secure external connectivity decisions.",
      "Enables monitoring of external data flows."
    ],
    goodLooksLike: [
      "Inventory of all external connections.",
      "Security requirements for external access.",
      "Regular review and approval of connections."
    ],
    evidence: [
      "External connection inventory.",
      "Connection approval records.",
      "Security assessment of external connections."
    ]
  },
  "ID.IM-04": {
    title: "Risk Impact Analysis",
    summary: "Assessment of potential consequences and business impact from identified cybersecurity risks.",
    why: [
      "Enables prioritization based on potential business impact.",
      "Supports informed risk acceptance decisions.",
      "Guides resource allocation for risk mitigation."
    ],
    goodLooksLike: [
      "Impact assessments for all identified risks.",
      "Business impact criteria consistently applied.",
      "Impact ratings tied to risk treatment priority."
    ],
    evidence: [
      "Risk impact assessment methodology.",
      "Risk register with impact ratings.",
      "Business impact analysis reports."
    ]
  },
  "GV.SC-02": {
    title: "Supply-Chain Roles & Responsibilities",
    summary: "Define who does what for supplier security across procurement, IT, and legal.",
    why: [
      "Reduces hand-offs and missed controls across teams.",
      "Clarifies who approves vendors and handles exceptions.",
      "Enables consistent requirements in contracts and onboarding."
    ],
    goodLooksLike: [
      "Named owner(s) for supplier risk, with backups.",
      "RACI documented and shared with stakeholders.",
      "Expectations communicated to critical suppliers."
    ],
    evidence: [
      "RACI or policy excerpt showing roles.",
      "Vendor onboarding SOP including security steps.",
      "Contract template with security addendum."
    ]
  },
  "GV.SC-04": {
    title: "Third-Party Risk Assessment",
    summary: "Evaluate and assess cybersecurity risks from external suppliers and partners.",
    why: [
      "Identifies security weaknesses in third-party relationships.",
      "Enables risk-based supplier selection and management.",
      "Reduces supply chain attack surface."
    ],
    goodLooksLike: [
      "Risk assessments for all critical suppliers.",
      "Standardized assessment criteria and questionnaires.",
      "Risk-based tiering of suppliers with controls."
    ],
    evidence: [
      "Third-party risk assessment reports.",
      "Assessment questionnaires and criteria.",
      "Supplier risk scoring and tiering documentation."
    ]
  },
  "GV.SC-06": {
    title: "Third-Party Monitoring",
    summary: "Ongoing oversight and monitoring of third-party cybersecurity compliance and performance.",
    why: [
      "Ensures suppliers maintain security standards over time.",
      "Detects changes in supplier risk posture.",
      "Enables timely remediation of supplier security issues."
    ],
    goodLooksLike: [
      "Regular monitoring of critical supplier security.",
      "Automated alerts for supplier security incidents.",
      "Periodic reassessments based on risk tier."
    ],
    evidence: [
      "Supplier monitoring reports and dashboards.",
      "Security incident notifications from suppliers.",
      "Reassessment schedules and completion records."
    ]
  },
  "GV.SC-07": {
    title: "Third-Party Contracts & Agreements",
    summary: "Contractual agreements addressing cybersecurity requirements for external parties.",
    why: [
      "Legally enforces security expectations with suppliers.",
      "Provides recourse for security failures.",
      "Clarifies data protection and breach notification obligations."
    ],
    goodLooksLike: [
      "Standard security clauses in all supplier contracts.",
      "Data processing agreements for data handlers.",
      "Breach notification and audit rights included."
    ],
    evidence: [
      "Contract templates with security clauses.",
      "Data processing agreements.",
      "Executed contracts with security provisions."
    ]
  },
  "GV.SC-05": {
    title: "Supplier Risk Monitoring",
    summary: "Continuously monitor supplier security posture and follow-up on gaps.",
    why: [
      "Catches changes in supplier risk between annual reviews.",
      "Supports faster containment when issues arise.",
      "Improves renewal decisions with current data."
    ],
    goodLooksLike: [
      "Defined risk tiers with monitoring cadence per tier.",
      "Tickets created for findings with owners & due dates.",
      "Quarterly reports covering top supplier risks."
    ],
    evidence: [
      "Recent supplier monitoring report.",
      "Tickets showing remediation & closure.",
      "List of suppliers by tier with cadence."
    ]
  },
  "GV.RR-02": {
    title: "Cyber Risk Roles",
    summary: "Assign and communicate cyber risk management responsibilities.",
    why: [
      "Clear accountability for risk decisions.",
      "Better alignment between business and security.",
      "Improves audit readiness and consistency."
    ],
    goodLooksLike: [
      "Named risk owners per domain/system.",
      "Risk committee with regular meetings.",
      "Escalation paths for high-risk exceptions."
    ],
    evidence: [
      "Charter of risk committee.",
      "Recent meeting notes & action logs.",
      "Register showing owners & due dates."
    ]
  },

  // Asset Management
  "ID.AM-01": {
    title: "Hardware Asset Inventory",
    summary: "Identify and track authorized devices with owners and criticality.",
    why: [
      "Foundation for patching and incident response.",
      "Prevents blind spots and shadow IT.",
      "Supports zero-trust and access decisions."
    ],
    goodLooksLike: [
      "Automated discovery feeds (e.g., EDR, MDM, CMDB).",
      "Owners and criticality fields populated.",
      "Reconciliation with network scans."
    ],
    evidence: [
      "Export of asset list with owners & tags.",
      "Procedures for monthly reconciliation.",
      "Screenshots of EDR/MDM inventory."
    ]
  },
  "ID.AM-02": {
    title: "Software & Service Inventory",
    summary: "Maintain a current catalog of software/SaaS with ownership.",
    why: [
      "Supports vulnerability management & licensing.",
      "Reduces unauthorized SaaS usage.",
      "Improves incident response scope & contacts."
    ],
    goodLooksLike: [
      "Central catalog with owners and data types.",
      "Intake for new apps with risk review steps.",
      "Periodic SaaS discovery & cleanup."
    ],
    evidence: [
      "App catalog export with owners.",
      "New-app intake SOP with security steps.",
      "Risk review artifacts for recent apps."
    ]
  },

  // Data Security
  "PR.DS-01": {
    title: "Identify Sensitive Data",
    summary: "Define what data is sensitive and where it lives.",
    why: [
      "Guides protection and access decisions.",
      "Reduces over-exposure and breach impact.",
      "Enables focused monitoring for high-risk data."
    ],
    goodLooksLike: [
      "Data categories (PII, PHI, PCI, IP) defined.",
      "Data locations cataloged and owners assigned.",
      "Periodic scans to find unknown stores."
    ],
    evidence: [
      "Data classification policy excerpt.",
      "Inventory of systems with sensitive data.",
      "Discovery scan results and fixes."
    ]
  },
  "PR.DS-02": {
    title: "Data-in-Transit Protection",
    summary: "Protect the confidentiality and integrity of data and signaling traffic moving across networks through encryption and secure protocols.",
    why: [
      "Prevents interception of customer, signaling, and management traffic.",
      "Protects telecom signaling planes (e.g., SS7, Diameter, SIP, GTP) from tampering.",
      "Supports regulatory and contractual confidentiality obligations."
    ],
    goodLooksLike: [
      "TLS/IPsec/MACsec enforced on internal and external links.",
      "Signaling traffic protected (e.g., DTLS, SIP-TLS, SEPP for 5G).",
      "Strong cipher suites and certificate lifecycle management in place."
    ],
    evidence: [
      "Network encryption configuration and architecture diagrams.",
      "Signaling/network protocol hardening standards.",
      "Certificate inventory and TLS scan reports."
    ]
  },
  "PR.DS-10": {
    title: "Lifecycle Protection",
    summary: "Apply protections from creation to archival and disposal.",
    why: [
      "Avoids stale sensitive copies lingering.",
      "Reduces cost and breach surface.",
      "Aligns storage with retention needs."
    ],
    goodLooksLike: [
      "Retention schedules by data type.",
      "Auto-archival and deletion flows.",
      "Secure disposal processes."
    ],
    evidence: [
      "Retention matrix.",
      "Job logs for archival/deletion.",
      "Vendor certs for media destruction."
    ]
  },

  // Access & Privilege
  "PR.AA-01": {
    title: "Strong Authentication (MFA/SSO)",
    summary: "Require MFA and SSO across core systems and remote/admin access.",
    why: [
      "Blocks most credential-stuffing attacks.",
      "Centralizes identity and access controls.",
      "Improves user experience and offboarding."
    ],
    goodLooksLike: [
      "MFA mandatory for privileged and remote access.",
      "SSO integrated with major apps.",
      "Risk-based challenges for sensitive actions."
    ],
    evidence: [
      "IdP policy screenshots (MFA enforced).",
      "SSO app list.",
      "Recent access review report."
    ]
  },
  "PR.AA-05": {
    title: "Least Privilege & PAM",
    summary: "Grant only the access needed; manage admin sessions securely.",
    why: [
      "Reduces blast radius of account compromise.",
      "Improves change traceability.",
      "Supports zero-trust objectives."
    ],
    goodLooksLike: [
      "Just-in-time admin access via PAM.",
      "Session recording for privileged actions.",
      "Quarterly access recertification."
    ],
    evidence: [
      "PAM config screenshots.",
      "Sample privileged session logs.",
      "Access review tickets."
    ]
  },
  "PR.AA-06": {
    title: "Access Reviews & Revocation",
    summary: "Review access regularly and promptly revoke when no longer needed.",
    why: [
      "Prevents stale access & insider risk.",
      "Meets audit & regulatory expectations.",
      "Cleans up shadow privileges."
    ],
    goodLooksLike: [
      "Joiner/Mover/Leaver automations.",
      "Quarterly recertification cadence.",
      "Documented revocation SLAs."
    ],
    evidence: [
      "Recent recertification results.",
      "Offboarding checklist with revocation steps.",
      "Tickets for revoked access."
    ]
  },

  // Platform Security — Secure Configuration & Hardening
  "PR.PS-02": {
    title: "Secure Configuration & System Hardening",
    summary: "Establish, apply, and maintain secure configuration baselines for hardware, software, and infrastructure (including telecom and operational systems).",
    why: [
      "Reduces attack surface by removing default and insecure settings.",
      "Provides a consistent, auditable security posture across systems.",
      "Supports detection of configuration drift and unauthorized change."
    ],
    goodLooksLike: [
      "Documented hardening standards (e.g., CIS / vendor benchmarks) applied to all critical systems.",
      "Automated configuration enforcement and drift detection.",
      "Periodic compliance reporting against baselines."
    ],
    evidence: [
      "Hardening / baseline standards documentation.",
      "Configuration compliance scan reports.",
      "Change tickets showing baseline-conformant builds."
    ]
  },
  "PR.PS-03": {
    title: "Endpoint Protection (EDR/AV)",
    summary: "Deploy EDR/AV everywhere and monitor alerts.",
    why: [
      "Detects malware and lateral movement.",
      "Supports incident investigations.",
      "Feeds detections with endpoint telemetry."
    ],
    goodLooksLike: [
      "EDR agent coverage ≥ 95%.",
      "Alert triage playbooks defined.",
      "Regular tuning to reduce noise."
    ],
    evidence: [
      "Coverage dashboard.",
      "Sample alert triage tickets.",
      "Tuning change log."
    ]
  },
  "PR.PS-04": {
    title: "Log Records & Continuous Monitoring",
    summary: "Generate and maintain log records to support continuous monitoring of systems and infrastructure.",
    why: [
      "Provides audit trail for security investigations.",
      "Enables detection of unauthorized activity.",
      "Supports regulatory and compliance requirements."
    ],
    goodLooksLike: [
      "Centralized log collection from critical systems.",
      "Log integrity and retention policies enforced.",
      "Logs feeding continuous monitoring and alerting."
    ],
    evidence: [
      "Log management architecture documentation.",
      "Log retention and integrity policies.",
      "SIEM or log aggregation platform configuration."
    ]
  },
  "PR.PS-01": {
    title: "Controlled Change Management",
    summary: "Review, test, approve, and roll back changes safely.",
    why: [
      "Prevents unplanned outages & regressions.",
      "Improves transparency and auditability.",
      "Aligns risk with change speed."
    ],
    goodLooksLike: [
      "CAB or risk-based approvals.",
      "Rollback plans attached to high-risk changes.",
      "Automated checks in CI/CD."
    ],
    evidence: [
      "Change tickets with approvals.",
      "Rollback procedures.",
      "CI/CD gate screenshots."
    ]
  },

  // Detect / Respond / Recover (only those used in your questionnaire)
  "DE.CM-01": {
    title: "Centralized Log Collection",
    summary: "Collect logs from key systems to a central platform.",
    why: [
      "Enables correlation and faster investigations.",
      "Improves detection coverage.",
      "Supports compliance and forensics."
    ],
    goodLooksLike: [
      "Onboarding runbook for new sources.",
      "Coverage metrics by source/type.",
      "Use cases mapped to log sources."
    ],
    evidence: [
      "SIEM source list & coverage.",
      "Onboarding checklist.",
      "Recent detection rules referencing logs."
    ]
  },
  "DE.AE-03": {
    title: "Event Analysis",
    summary: "Analyze events to identify anomalies and potential incidents.",
    why: [
      "Catches early indicators of compromise.",
      "Reduces false positives with tuning.",
      "Feeds continuous improvement."
    ],
    goodLooksLike: [
      "Documented alert triage workflow.",
      "Tuning backlog with owners.",
      "MITRE coverage tracking."
    ],
    evidence: [
      "Triage SOP.",
      "Example tuned rules.",
      "Coverage dashboard."
    ]
  },
  "DE.CM-09": {
    title: "Endpoint Telemetry Analysis",
    summary: "Analyze endpoint events for suspicious behavior and containment.",
    why: [
      "Detects ransomware, lateral movement.",
      "Speeds containment on endpoints.",
      "Supports threat hunting."
    ],
    goodLooksLike: [
      "Use cases for endpoint events.",
      "Rapid isolation procedures.",
      "Hunt queries maintained."
    ],
    evidence: [
      "Endpoint detection rules.",
      "Isolation playbook.",
      "Hunt notebooks or queries."
    ]
  },
  "DE.AE-06": {
    title: "Alert Triage & Escalation",
    summary: "Classify, escalate, and resolve alerts with SLAs.",
    why: [
      "Improves response time & consistency.",
      "Focuses effort on what matters.",
      "Creates measurable outcomes."
    ],
    goodLooksLike: [
      "Severity model with SLAs.",
      "On-call schedule & runbooks.",
      "Post-triage metrics and QA."
    ],
    evidence: [
      "Escalation matrix.",
      "Sample triage tickets.",
      "MTTR metrics."
    ]
  },
  "DE.AE-07": {
    title: "Threat Intel for Detection",
    summary: "Use CTI to enrich detections and prioritize threats.",
    why: [
      "Focuses on active threats.",
      "Improves fidelity of alerts.",
      "Accelerates investigations."
    ],
    goodLooksLike: [
      "Feeds integrated to SIEM/EDR.",
      "Intel-driven detection backlog.",
      "Hunting informed by intel."
    ],
    evidence: [
      "Feed config screenshots.",
      "Detection updates referencing intel.",
      "Hunt reports."
    ]
  },
  "DE.AE-08": {
    title: "Continuous Detection Tuning",
    summary: "Iteratively improve detection content and coverage.",
    why: [
      "Reduces alert fatigue.",
      "Catches evolving attacker behavior.",
      "Builds maturity over time."
    ],
    goodLooksLike: [
      "Quarterly rule review cadence.",
      "Coverage tracked vs. MITRE/risks.",
      "Change log for rules."
    ],
    evidence: [
      "Rule change history.",
      "Coverage heatmap.",
      "Retros on noisy rules."
    ]
  },

  "RS.MA-01": {
    title: "Incident Response Plan",
    summary: "Define roles, contacts, and steps for handling incidents.",
    why: [
      "Avoids chaos during breaches.",
      "Clarifies who decides and when.",
      "Meets regulatory expectations."
    ],
    goodLooksLike: [
      "Named roles & contact tree.",
      "Table-top exercises run.",
      "Integration with comms & legal."
    ],
    evidence: [
      "IR plan excerpt.",
      "Exercise report with actions.",
      "On-call roster."
    ]
  },
  "RS.CO-01": {
    title: "Incident Communications",
    summary: "Coordinate internal/external communications during incidents.",
    why: [
      "Prevents misinformation & delays.",
      "Aligns with legal/regulatory needs.",
      "Builds trust with stakeholders."
    ],
    goodLooksLike: [
      "Pre-approved messaging templates.",
      "Stakeholder map with contacts.",
      "Escalation paths defined."
    ],
    evidence: [
      "Templates & comms matrix.",
      "Sample comms from exercises.",
      "Regulatory notification SOP."
    ]
  },
  "RS.CO-03": {
    title: "External Stakeholder Engagement",
    summary: "Coordinate with regulators, law enforcement, and partners when needed.",
    why: [
      "Reduces legal risk.",
      "Improves response coordination.",
      "Meets contractual obligations."
    ],
    goodLooksLike: [
      "When/How to notify defined.",
      "POCs established in advance.",
      "Templates aligned to requirements."
    ],
    evidence: [
      "Notification flowchart.",
      "POC list with updates.",
      "Recent engagement record (sanitized)."
    ]
  },
  "RS.MI-01": {
    title: "Containment",
    summary: "Limit attacker movement and damage quickly.",
    why: [
      "Buys time to investigate.",
      "Reduces data loss & downtime.",
      "Prepares for eradication."
    ],
    goodLooksLike: [
      "Playbooks for common scenarios.",
      "Endpoint/network isolation steps.",
      "Decision tree for business impact."
    ],
    evidence: [
      "Containment playbook.",
      "Recent containment ticket.",
      "Post-incident review excerpt."
    ]
  },
  "RS.MI-02": {
    title: "Eradication",
    summary: "Remove the root cause and malicious artifacts.",
    why: [
      "Prevents reinfection.",
      "Restores normal operations safely.",
      "Closes vulnerabilities found."
    ],
    goodLooksLike: [
      "Procedures to remove malware & backdoors.",
      "Root cause analysis performed.",
      "Patching/hardening applied."
    ],
    evidence: [
      "Eradication checklist.",
      "RCA document.",
      "Change tickets showing fixes."
    ]
  },
  "RS.MI-03": {
    title: "Recovery",
    summary: "Restore systems and validate they are clean & functional.",
    why: [
      "Minimizes downtime.",
      "Ensures safe return to service.",
      "Captures lessons for next time."
    ],
    goodLooksLike: [
      "Staged recovery with validation checks.",
      "Business sign-off steps.",
      "Tracking of recovery KPIs (RTO/RPO)."
    ],
    evidence: [
      "Recovery runbook.",
      "Validation checklist.",
      "Metrics dashboard."
    ]
  },

  // Backup / Recovery
  "PR.DS-11": {
    title: "Backup Protection",
    summary: "Protect backups with immutability, isolation, and access controls.",
    why: [
      "Critical against ransomware.",
      "Enables reliable restoration.",
      "Reduces data loss."
    ],
    goodLooksLike: [
      "Immutable or offline copies.",
      "Access separated from prod credentials.",
      "Regular restore testing."
    ],
    evidence: [
      "Backup policy excerpt.",
      "Immutable storage config.",
      "Restore test results."
    ]
  },
  "RC.RP": {
    title: "Recovery Planning",
    summary: "Plan for restoring services and data after disruption.",
    why: [
      "Shortens downtime.",
      "Aligns teams on priorities.",
      "Supports regulatory requirements."
    ],
    goodLooksLike: [
      "Defined RTO/RPO targets.",
      "Runbooks by system tier.",
      "Exercises and improvements."
    ],
    evidence: [
      "Recovery plan snippet.",
      "Runbook sample.",
      "Exercise report."
    ]
  },
  "RC.RP-01": {
    title: "Execute Recovery",
    summary: "Run recovery plans consistently and track outcomes.",
    why: [
      "Proves resilience in practice.",
      "Improves with each exercise.",
      "Builds stakeholder confidence."
    ],
    goodLooksLike: [
      "Execution checklists per system.",
      "Sign-off by owners.",
      "Post-recovery review items."
    ],
    evidence: [
      "Recent execution record.",
      "Sign-off artifacts.",
      "Improvement backlog."
    ]
  },
  "RC.CO-01": {
    title: "Lessons Learned",
    summary: "Capture and share lessons after incidents or outages.",
    why: [
      "Prevents repeat issues.",
      "Improves playbooks and controls.",
      "Builds a learning culture."
    ],
    goodLooksLike: [
      "Structured post-mortems.",
      "Themes & owners tracked.",
      "Updates fed into roadmaps."
    ],
    evidence: [
      "Post-mortem template & sample.",
      "Backlog items created.",
      "Roadmap updates referencing lessons."
    ]
  },

  // Information Protection & Response
  "PR.IR-01": {
    title: "Network Segmentation",
    summary: "Segment networks to isolate critical assets and limit lateral movement.",
    why: [
      "Contains breaches and reduces attack spread.",
      "Protects sensitive systems from less secure areas.",
      "Enables more granular security controls."
    ],
    goodLooksLike: [
      "Network zones defined by criticality and trust level.",
      "Firewall rules enforcing segmentation boundaries.",
      "Regular review and testing of segmentation effectiveness."
    ],
    evidence: [
      "Network segmentation diagram.",
      "Firewall rule documentation.",
      "Penetration test results validating segmentation."
    ]
  },

  // Awareness & Training
  "PR.AT-01": {
    title: "Security Awareness Training",
    summary: "Provide regular cybersecurity awareness training to all users.",
    why: [
      "Reduces human error and social engineering success.",
      "Creates security-conscious culture.",
      "Meets regulatory compliance requirements."
    ],
    goodLooksLike: [
      "Annual mandatory training for all staff.",
      "Role-specific training for privileged users.",
      "Regular phishing simulations and feedback."
    ],
    evidence: [
      "Training completion reports and metrics.",
      "Training content and curriculum.",
      "Phishing simulation results and trends."
    ]
  },
  "PR.AT-02": {
    title: "Privileged User Training",
    summary: "Provide specialized security training for users with elevated privileges.",
    why: [
      "Privileged users are high-value targets.",
      "Reduces risk of insider threats and mistakes.",
      "Ensures understanding of elevated responsibilities."
    ],
    goodLooksLike: [
      "Specialized training for administrators.",
      "Training on secure administration practices.",
      "Regular refreshers on emerging threats."
    ],
    evidence: [
      "Privileged user training records.",
      "Specialized training materials.",
      "Acknowledgment of privileged access responsibilities."
    ]
  },

  // CSF 2.0 — Governance (telecom_v2)
  "GV.OV-01": {
    title: "Cybersecurity Oversight",
    summary: "Leadership oversees cybersecurity strategy, governance, and risk management.",
    why: [
      "Ensures cybersecurity is aligned with organizational priorities.",
      "Improves accountability for security outcomes.",
      "Enables leadership to guide security investments."
    ],
    goodLooksLike: [
      "Executive or board-level security oversight.",
      "Regular cybersecurity briefings.",
      "Documented security governance structure."
    ],
    evidence: [
      "Cybersecurity governance charter.",
      "Executive cybersecurity oversight documentation.",
      "Security leadership organizational chart."
    ]
  },
  "GV.PO-01": {
    title: "Cybersecurity Policy",
    summary: "The organization maintains cybersecurity policies governing protection of telecom systems and data.",
    why: [
      "Establishes clear security expectations.",
      "Provides guidance for operational teams.",
      "Supports regulatory compliance."
    ],
    goodLooksLike: [
      "Approved cybersecurity policies.",
      "Regular policy review cycle.",
      "Policies aligned with industry frameworks."
    ],
    evidence: [
      "Enterprise cybersecurity policy.",
      "Policy governance procedures.",
      "Policy compliance reports."
    ]
  },
  "GV.OC-03": {
    title: "Regulatory & Legal Requirements",
    summary: "Regulatory and legal cybersecurity requirements are identified and integrated into operations.",
    why: [
      "Ensures compliance with applicable regulations.",
      "Reduces legal and regulatory risk.",
      "Guides security control implementation."
    ],
    goodLooksLike: [
      "Regulatory compliance register.",
      "Compliance monitoring procedures.",
      "Regulatory mapping to security controls."
    ],
    evidence: [
      "Regulatory compliance register.",
      "Compliance monitoring procedures.",
      "Audit reports."
    ]
  },
  "GV.ME-01": {
    title: "Cybersecurity Performance Measurement",
    summary: "Leadership measures, reviews, and improves cybersecurity performance across the organization.",
    why: [
      "Enables data-driven security decisions.",
      "Tracks progress toward security objectives.",
      "Supports continuous improvement."
    ],
    goodLooksLike: [
      "Cybersecurity KPI dashboards.",
      "Regular executive security briefings.",
      "Security maturity assessments."
    ],
    evidence: [
      "Cybersecurity KPI dashboards.",
      "Security performance reports.",
      "Security program review documentation."
    ]
  },

  // CSF 2.0 — Identify (telecom_v2)
  "ID.AM-03": {
    title: "Data Flow Mapping",
    summary: "Sensitive data flows across telecom systems are documented and understood.",
    why: [
      "Improves visibility into sensitive data movement.",
      "Enables better security controls.",
      "Supports regulatory compliance."
    ],
    goodLooksLike: [
      "Documented data flow diagrams.",
      "Data classification processes.",
      "Regular updates to data flow documentation."
    ],
    evidence: [
      "Data flow diagrams.",
      "Data classification documentation.",
      "Data mapping reports."
    ]
  },
  "ID.RA-02": {
    title: "Threat Intelligence",
    summary: "The organization identifies cybersecurity threats that could impact telecom infrastructure.",
    why: [
      "Focuses defenses on real threats.",
      "Improves detection and response capabilities.",
      "Supports risk-informed decisions."
    ],
    goodLooksLike: [
      "Threat intelligence subscriptions.",
      "Threat analysis integrated into security operations.",
      "Regular threat briefings."
    ],
    evidence: [
      "Threat intelligence reports.",
      "Threat monitoring procedures.",
      "Threat analysis documentation."
    ]
  },
  "ID.RA-05": {
    title: "Threat & Vulnerability Impact Analysis",
    summary: "The organization evaluates the potential impact of cybersecurity threats and vulnerabilities on operations.",
    why: [
      "Enables risk-based prioritization.",
      "Supports informed mitigation decisions.",
      "Improves operational resilience planning."
    ],
    goodLooksLike: [
      "Risk impact analysis documentation.",
      "Operational impact assessments.",
      "Impact ratings integrated into risk register."
    ],
    evidence: [
      "Risk impact analysis documentation.",
      "Cyber risk assessments.",
      "Business impact analysis reports."
    ]
  },

  // CSF 2.0 — Detect (telecom_v2)
  "DE.CM-03": {
    title: "Log Collection & Correlation",
    summary: "Security logs from telecom systems are collected, analyzed, and correlated.",
    why: [
      "Enables detection of complex attacks.",
      "Supports incident investigation.",
      "Improves operational visibility."
    ],
    goodLooksLike: [
      "Centralized log management platform.",
      "Log retention policies.",
      "Correlation rules across systems."
    ],
    evidence: [
      "Log management architecture documentation.",
      "SIEM configuration exports.",
      "Log retention policies."
    ]
  },
  "DE.AE-02": {
    title: "Security Event Analysis",
    summary: "Detected security events are analyzed to determine risk and required response.",
    why: [
      "Identifies real threats from noise.",
      "Supports timely incident response.",
      "Reduces dwell time of attackers."
    ],
    goodLooksLike: [
      "Defined event analysis procedures.",
      "Investigation runbooks.",
      "Documented analysis reports."
    ],
    evidence: [
      "Security event analysis procedures.",
      "Incident investigation documentation.",
      "Event analysis reports."
    ]
  },

  // CSF 2.0 — Respond (telecom_v2)
  "RS.AN-03": {
    title: "Incident Investigation",
    summary: "Security teams investigate incidents to determine root cause and impact.",
    why: [
      "Identifies attack methods.",
      "Prevents recurrence.",
      "Supports recovery actions."
    ],
    goodLooksLike: [
      "Incident investigation procedures.",
      "Forensic capabilities.",
      "Documented investigation reports."
    ],
    evidence: [
      "Incident investigation procedures.",
      "Forensic investigation guidelines.",
      "Incident investigation reports."
    ]
  },

  // ── OWASP Top 10 for LLM Applications 2025 ─────────────────────────
  "OWASP-LLM01": {
    title: "LLM01: Prompt Injection",
    summary: "Manipulating LLMs through crafted inputs that cause unintended actions, bypassing safeguards or altering outputs.",
    why: ["#1 vulnerability in OWASP Top 10 for LLM.", "Can bypass all other security controls.", "Enables data exfiltration and unauthorized actions."],
    goodLooksLike: ["Multi-layer input validation and sanitization.", "System prompt isolation from user input.", "Regular prompt injection red-team testing."],
    evidence: ["Prompt injection prevention architecture.", "Red team test results for injection attacks.", "Input validation and filtering configurations."]
  },
  "OWASP-LLM02": {
    title: "LLM02: Sensitive Information Disclosure",
    summary: "LLM outputs may inadvertently reveal sensitive data including PII, credentials, or proprietary information from training data or context.",
    why: ["Training data may contain sensitive information.", "RAG pipelines can leak restricted documents.", "Prompt manipulation can extract hidden context."],
    goodLooksLike: ["Output filtering for PII and sensitive data.", "Data classification applied to training/RAG sources.", "Regular testing for data leakage via prompts."],
    evidence: ["Output filtering configurations.", "PII detection/redaction pipelines.", "Data leakage test results."]
  },
  "OWASP-LLM03": {
    title: "LLM03: Supply Chain Vulnerabilities",
    summary: "Risks from third-party model components, training data, plugins, and deployment platforms that can introduce vulnerabilities.",
    why: ["Pre-trained models may contain backdoors.", "Third-party plugins can be compromised.", "Supply chain attacks target model distribution."],
    goodLooksLike: ["Model provenance verification before deployment.", "Vendor security assessments for AI providers.", "SBOM-equivalent documentation for AI components."],
    evidence: ["Model provenance verification records.", "Third-party AI vendor assessments.", "AI supply chain risk assessment reports."]
  },
  "OWASP-LLM04": {
    title: "LLM04: Data and Model Poisoning",
    summary: "Tampering with training data or model weights to introduce backdoors, biases, or degraded performance.",
    why: ["Poisoned data creates subtle, hard-to-detect backdoors.", "Compromises model integrity and trustworthiness.", "Can lead to targeted manipulation of outputs."],
    goodLooksLike: ["Data validation before training ingestion.", "Anomaly detection on training datasets.", "Model behavior monitoring post-deployment."],
    evidence: ["Training data validation procedures.", "Anomaly detection configurations.", "Model behavior drift monitoring reports."]
  },
  "OWASP-LLM05": {
    title: "LLM05: Improper Output Handling",
    summary: "Failure to validate LLM outputs before passing to downstream systems, enabling XSS, SSRF, code injection, or privilege escalation.",
    why: ["LLM outputs may contain executable content.", "Downstream systems may trust LLM output implicitly.", "Enables indirect injection attack chains."],
    goodLooksLike: ["Output sanitization before rendering or API calls.", "Structured output validation schemas.", "Content safety classifiers on responses."],
    evidence: ["Output validation pipeline documentation.", "Content filtering and sanitization configs.", "Security testing for downstream injection."]
  },
  "OWASP-LLM06": {
    title: "LLM06: Excessive Agency",
    summary: "LLM systems granted too many permissions, excessive autonomy, or ability to take impactful actions without human oversight.",
    why: ["Over-privileged agents can cause unintended damage.", "Lack of human-in-the-loop for critical decisions.", "Amplifies impact of prompt injection attacks."],
    goodLooksLike: ["Least-privilege permissions for AI agents.", "Human approval required for high-impact actions.", "Granular tool/API access controls."],
    evidence: ["AI agent permission configurations.", "Human-in-the-loop approval workflows.", "Access scope documentation for AI tools."]
  },
  "OWASP-LLM07": {
    title: "LLM07: System Prompt Leakage",
    summary: "Unintended exposure of system prompts revealing security controls, business logic, or sensitive instructions to users.",
    why: ["System prompts contain security-critical instructions.", "Leaked prompts enable targeted bypass attacks.", "Exposes proprietary prompt engineering IP."],
    goodLooksLike: ["System prompts architecturally protected.", "Anti-extraction measures tested regularly.", "Monitoring for prompt leakage attempts."],
    evidence: ["System prompt security architecture.", "Prompt extraction red team results.", "Leakage attempt monitoring dashboards."]
  },
  "OWASP-LLM08": {
    title: "LLM08: Vector and Embedding Weaknesses",
    summary: "Vulnerabilities in RAG pipelines, vector databases, and embedding processes that can be exploited for unauthorized access or data poisoning.",
    why: ["Vector DBs often lack fine-grained access control.", "Embedding poisoning can manipulate retrieval.", "RAG pipelines may bypass document-level permissions."],
    goodLooksLike: ["Access controls preserving source document permissions.", "Vector DB integrity monitoring.", "Embedding pipeline validation."],
    evidence: ["RAG security architecture review.", "Vector DB access control configs.", "Embedding integrity verification procedures."]
  },
  "OWASP-LLM09": {
    title: "LLM09: Misinformation",
    summary: "LLMs generating factually incorrect, misleading, or fabricated content (hallucinations) presented as authoritative.",
    why: ["Hallucinations can cause real-world harm.", "Erodes trust in AI-powered systems.", "Creates legal liability from incorrect outputs."],
    goodLooksLike: ["Factual grounding via citations/sources.", "Confidence scoring on AI outputs.", "Human review for high-stakes decisions."],
    evidence: ["Hallucination detection mechanisms.", "Confidence scoring implementation.", "Human review workflows and accuracy metrics."]
  },
  "OWASP-LLM10": {
    title: "LLM10: Unbounded Consumption",
    summary: "Attacks or misuse causing excessive resource consumption, leading to denial-of-service or runaway costs.",
    why: ["AI compute is expensive and exploitable.", "Uncontrolled usage causes massive cost overruns.", "DoS attacks can exhaust AI infrastructure."],
    goodLooksLike: ["Rate limiting on all AI endpoints.", "Budget caps and spending alerts.", "Resource quotas per user/application."],
    evidence: ["Rate limiting configurations.", "Cost monitoring and budget alerting.", "Resource usage quotas and dashboards."]
  },

  // ── NIST AI 600-1 References ──────────────────────────────────────────
  "NIST-AI-GV-1.1-001": {
    title: "NIST AI 600-1 GV-1.1-001: AI Governance",
    summary: "Establish governance structures for AI systems including policies, accountability, and oversight mechanisms.",
    why: ["Sets organizational boundaries for AI adoption.", "Ensures executive accountability for AI risks.", "Required by NIST AI RMF Govern function."],
    goodLooksLike: ["Board-approved AI governance policy.", "Named AI risk owner at executive level.", "Regular AI governance reviews."],
    evidence: ["AI governance policy document.", "AI risk committee charter.", "AI oversight review meeting minutes."]
  },
  "NIST-AI-GV-1.1-002": {
    title: "NIST AI 600-1 GV-1.1-002: AI Asset Management",
    summary: "Maintain inventory and classification of AI systems, models, and data dependencies.",
    why: ["Cannot secure what you don't know about.", "Enables risk-based prioritization.", "Supports compliance and audit readiness."],
    goodLooksLike: ["Complete AI system inventory maintained.", "Model metadata tracked (version, source, purpose).", "Shadow AI discovery processes in place."],
    evidence: ["AI system inventory/register.", "Model cards or documentation.", "Shadow AI discovery scan results."]
  },
  "NIST-AI-GV-1.1-003": {
    title: "NIST AI 600-1 GV-1.1-003: AI Regulatory Mapping",
    summary: "Map AI systems to applicable regulatory requirements and maintain compliance evidence.",
    why: ["Avoids regulatory penalties.", "Demonstrates responsible AI deployment.", "Required by emerging AI legislation (EU AI Act, etc.)."],
    goodLooksLike: ["AI compliance register maintained.", "Requirements mapped from applicable frameworks.", "Regular compliance gap assessments."],
    evidence: ["AI regulatory compliance matrix.", "Gap assessment reports.", "Legal review documentation."]
  },
  "NIST-AI-GV-1.2-001": {
    title: "NIST AI 600-1 GV-1.2-001: AI Risk Assessment",
    summary: "Conduct risk assessments for AI systems covering model, data, operational, and ethical risks.",
    why: ["Identifies AI-specific threats before deployment.", "Supports informed go/no-go decisions.", "Prioritizes risk mitigation investments."],
    goodLooksLike: ["AI risk assessment framework documented.", "Risk assessments before model deployment.", "AI entries in enterprise risk register."],
    evidence: ["AI risk assessment reports.", "Model risk evaluation templates.", "AI risk register entries."]
  },
  "NIST-AI-GV-1.2-002": {
    title: "NIST AI 600-1 GV-1.2-002: AI Incident Management",
    summary: "Establish incident response procedures for AI-specific failures, attacks, and harmful outputs.",
    why: ["AI failures require specialized response.", "Enables rapid containment of AI incidents.", "Supports learning from AI events."],
    goodLooksLike: ["AI incident playbooks documented.", "Clear escalation paths for AI failures.", "Post-incident reviews for AI events."],
    evidence: ["AI incident response procedures.", "AI incident log and post-mortems.", "Tabletop exercise results."]
  },
  "NIST-AI-MS-2.3-001": {
    title: "NIST AI 600-1 MS-2.3-001: AI Risk Monitoring",
    summary: "Continuously monitor AI systems for emerging risks, model drift, and performance degradation.",
    why: ["AI risks evolve post-deployment.", "Model drift can degrade safety controls.", "Enables proactive risk management."],
    goodLooksLike: ["Continuous AI risk monitoring dashboards.", "Model performance baselines and alerts.", "Regular risk reassessment cadence."],
    evidence: ["AI monitoring dashboard screenshots.", "Model drift detection reports.", "Risk reassessment documentation."]
  },
  "NIST-AI-MS-2.3-002": {
    title: "NIST AI 600-1 MS-2.3-002: AI Behavioral Monitoring",
    summary: "Monitor AI system behavior, log interactions, and detect anomalous usage patterns.",
    why: ["Enables detection of AI-specific attacks.", "Supports forensic investigation.", "Identifies abuse and misuse patterns."],
    goodLooksLike: ["Comprehensive AI interaction logging.", "Behavioral anomaly detection rules.", "Alerting for suspicious activity."],
    evidence: ["AI monitoring architecture.", "Logging configurations and retention.", "Anomaly detection alert rules."]
  },
  "NIST-AI-MS-2.3-003": {
    title: "NIST AI 600-1 MS-2.3-003: AI Output Integrity",
    summary: "Monitor and control AI output quality, accuracy, and factual grounding.",
    why: ["Hallucinations can cause real-world harm.", "Output quality degrades over time.", "Legal liability from incorrect AI outputs."],
    goodLooksLike: ["Hallucination detection mechanisms.", "Confidence scoring on outputs.", "Human review for high-stakes decisions."],
    evidence: ["Output quality monitoring configs.", "Confidence scoring implementation.", "Human review workflow documentation."]
  },
  "NIST-AI-MS-2.3-004": {
    title: "NIST AI 600-1 MS-2.3-004: AI Resource Management",
    summary: "Manage computational resources, costs, and capacity for AI systems to prevent abuse.",
    why: ["Uncontrolled AI usage causes cost overruns.", "Resource exhaustion enables denial-of-service.", "Supports sustainable AI operations."],
    goodLooksLike: ["Rate limiting on AI endpoints.", "Budget alerts and spending caps.", "Resource quotas per application."],
    evidence: ["Rate limiting configurations.", "Budget monitoring setup.", "Resource quota documentation."]
  },
  "NIST-AI-MS-2.5-001": {
    title: "NIST AI 600-1 MS-2.5-001: Third-Party AI Due Diligence",
    summary: "Assess security and trustworthiness of third-party AI models and API providers.",
    why: ["Third-party models may contain backdoors.", "API providers may access or retain data.", "Supply chain attacks target AI distribution."],
    goodLooksLike: ["Vendor security assessments for AI providers.", "Model provenance verification.", "Contractual security requirements."],
    evidence: ["Third-party AI vendor assessments.", "Model evaluation reports.", "AI vendor security contracts."]
  },
  "NIST-AI-MS-2.5-002": {
    title: "NIST AI 600-1 MS-2.5-002: AI Model Integrity",
    summary: "Verify provenance, integrity, and authenticity of AI models throughout their lifecycle.",
    why: ["Prevents use of tampered models.", "Supports audit trails for model decisions.", "Enables rollback to known-good versions."],
    goodLooksLike: ["Model signing and hash verification.", "Version control for deployed models.", "Provenance documentation for model lineage."],
    evidence: ["Model integrity verification procedures.", "Version control system for models.", "Model provenance documentation."]
  },
  "NIST-AI-MS-2.6-001": {
    title: "NIST AI 600-1 MS-2.6-001: Training Data Governance",
    summary: "Govern data used for AI training, ensuring quality, provenance, and integrity.",
    why: ["Data quality directly impacts model behavior.", "Prevents bias, toxicity, and compliance violations.", "Supports model reproducibility."],
    goodLooksLike: ["Training data provenance documented.", "Data quality and bias assessments.", "Consent/licensing verified for training data."],
    evidence: ["Training data governance policy.", "Data provenance and lineage records.", "Bias assessment reports."]
  },
  "NIST-AI-MS-2.6-002": {
    title: "NIST AI 600-1 MS-2.6-002: Data Poisoning Prevention",
    summary: "Detect and prevent data poisoning across training, fine-tuning, and inference pipelines.",
    why: ["Poisoned data creates backdoors in models.", "Subtle poisoning is hard to detect post-training.", "Compromises model trustworthiness."],
    goodLooksLike: ["Data validation before training ingestion.", "Anomaly detection on datasets.", "Provenance verification for data sources."],
    evidence: ["Data validation procedures.", "Anomaly detection configurations.", "Training data provenance records."]
  },
  "NIST-AI-MS-2.6-004": {
    title: "NIST AI 600-1 MS-2.6-004: RAG Pipeline Security",
    summary: "Secure retrieval-augmented generation pipelines and knowledge bases from poisoning and unauthorized access.",
    why: ["Poisoned knowledge bases produce incorrect outputs.", "Unauthorized access can leak sensitive documents.", "RAG is a growing attack vector."],
    goodLooksLike: ["Access controls on knowledge base documents.", "Input validation for document ingestion.", "Monitoring for knowledge base modifications."],
    evidence: ["RAG security architecture documentation.", "Knowledge base access control configs.", "Document ingestion validation procedures."]
  },
  "NIST-AI-MS-2.7-001": {
    title: "NIST AI 600-1 MS-2.7-001: AI Adversarial Testing",
    summary: "Conduct adversarial testing and red teaming exercises targeting AI/LLM systems.",
    why: ["Discovers AI-specific vulnerabilities.", "Tests effectiveness of AI security controls.", "Builds organizational AI security expertise."],
    goodLooksLike: ["Regular AI red teaming exercises.", "Testing covers OWASP Top 10 for LLM.", "Findings tracked and remediated."],
    evidence: ["AI red team exercise reports.", "Adversarial testing methodology.", "Finding remediation tracking."]
  },
  "NIST-AI-MS-2.7-002": {
    title: "NIST AI 600-1 MS-2.7-002: AI Data Protection",
    summary: "Protect sensitive data in AI pipelines including inputs, outputs, training data, and logs.",
    why: ["Prevents unintended data exposure.", "Ensures compliance with data protection regulations.", "Reduces training data leakage risk."],
    goodLooksLike: ["Data classification applied to AI pipelines.", "PII filtering before model input.", "Data lineage tracked through AI workflows."],
    evidence: ["Data flow diagrams for AI pipelines.", "Data classification records.", "PII detection/filtering configurations."]
  },
  "NIST-AI-MS-2.7-007": {
    title: "NIST AI 600-1 MS-2.7-007: Prompt Injection Countermeasures",
    summary: "Implement multi-layered defenses against direct and indirect prompt injection attacks.",
    why: ["Prompt injection is the #1 LLM vulnerability.", "Indirect injection via RAG/tools is hard to detect.", "Can bypass all other security controls."],
    goodLooksLike: ["Multi-layer input validation.", "Indirect injection detection for retrieved content.", "Regular prompt injection red-team testing."],
    evidence: ["Prompt injection defense architecture.", "Red team testing results.", "Input validation configurations."]
  },
  "NIST-AI-MS-2.7-008": {
    title: "NIST AI 600-1 MS-2.7-008: AI Authentication & Access",
    summary: "Enforce authentication and authorization for AI system endpoints and administrative interfaces.",
    why: ["Prevents unauthorized AI capability use.", "Supports usage tracking and accountability.", "Enables rate limiting and abuse prevention."],
    goodLooksLike: ["Authentication on all AI endpoints.", "Role-based access to AI capabilities.", "API key management and rotation."],
    evidence: ["AI access control configurations.", "Authentication architecture docs.", "API key management procedures."]
  },
  "NIST-AI-MS-2.7-009": {
    title: "NIST AI 600-1 MS-2.7-009: AI Agent Least Privilege",
    summary: "Apply least-privilege to AI agent permissions, tool access, and system integrations.",
    why: ["Limits blast radius of compromised agents.", "Prevents unauthorized AI actions.", "Defense-in-depth for AI autonomy."],
    goodLooksLike: ["AI agents have minimal required permissions.", "Tool/API access scoped and reviewed.", "Human approval for high-impact actions."],
    evidence: ["AI agent permission configs.", "Access control docs for AI tools.", "Human approval workflow documentation."]
  },
  "NIST-AI-MS-2.7-010": {
    title: "NIST AI 600-1 MS-2.7-010: System Prompt Protection",
    summary: "Protect system prompts from extraction, manipulation, and unauthorized disclosure.",
    why: ["System prompts contain security-critical instructions.", "Extraction enables targeted bypass attacks.", "Protects prompt engineering IP."],
    goodLooksLike: ["System prompts stored securely.", "Anti-extraction measures tested regularly.", "Monitoring for prompt leakage attempts."],
    evidence: ["System prompt security architecture.", "Prompt extraction test results.", "Secure prompt storage configurations."]
  },
  "NIST-AI-GV-1.1-004": {
    title: "NIST AI 600-1 GV-1.1-004: AI Bias & Fairness Governance",
    summary: "Establish governance controls to identify, measure, and mitigate bias and fairness risks in AI systems.",
    why: ["Biased AI outputs cause reputational and legal harm.", "Fairness requirements vary by jurisdiction and use case.", "Proactive bias management builds stakeholder trust."],
    goodLooksLike: ["Bias assessment framework documented and applied.", "Fairness metrics defined per use case.", "Regular bias audits with documented results."],
    evidence: ["Bias assessment reports.", "Fairness metric definitions and thresholds.", "Bias audit and remediation records."]
  },
  "NIST-AI-GV-1.2-003": {
    title: "NIST AI 600-1 GV-1.2-003: AI Explainability & Transparency",
    summary: "Ensure AI system decisions can be explained and that transparency requirements are met for stakeholders.",
    why: ["Regulatory frameworks increasingly require explainability.", "Builds trust with affected individuals.", "Supports human oversight and accountability."],
    goodLooksLike: ["Explainability requirements defined by risk tier.", "Interpretability techniques applied to high-risk models.", "Transparency documentation available to stakeholders."],
    evidence: ["Explainability policy and standards.", "Model interpretability reports.", "Stakeholder transparency disclosures."]
  },
  "NIST-AI-GV-1.2-004": {
    title: "NIST AI 600-1 GV-1.2-004: AI Stakeholder Communication",
    summary: "Communicate AI risks, decisions, and governance outcomes to internal and external stakeholders.",
    why: ["Stakeholders need visibility into AI risk posture.", "Transparency builds trust and supports compliance.", "Enables informed decision-making across the organization."],
    goodLooksLike: ["Regular AI governance reports to leadership.", "Stakeholder-appropriate risk communications.", "Public disclosure of AI use where required."],
    evidence: ["AI governance reporting cadence and artifacts.", "Stakeholder communication templates.", "Public AI transparency statements."]
  },
  "NIST-AI-MS-2.7-003": {
    title: "NIST AI 600-1 MS-2.7-003: Sensitive Data Leakage Prevention",
    summary: "Prevent AI systems from leaking sensitive data through outputs, logs, or model memorization.",
    why: ["LLMs can memorize and regurgitate training data.", "Output-side data leakage bypasses input controls.", "Regulatory penalties for PII exposure via AI."],
    goodLooksLike: ["Output filtering for PII and sensitive data.", "Model memorization testing performed.", "Data loss prevention integrated into AI pipelines."],
    evidence: ["Output filtering configurations.", "Memorization and extraction test results.", "DLP integration documentation."]
  },
  "NIST-AI-MS-2.7-004": {
    title: "NIST AI 600-1 MS-2.7-004: RAG Knowledge Base Security",
    summary: "Secure knowledge bases and document stores used in retrieval-augmented generation pipelines.",
    why: ["Poisoned documents produce harmful AI outputs.", "Unauthorized access leaks sensitive information.", "Knowledge base integrity is critical for RAG accuracy."],
    goodLooksLike: ["Access controls on document ingestion.", "Document provenance verification.", "Monitoring for unauthorized knowledge base changes."],
    evidence: ["Knowledge base access control configs.", "Document ingestion validation procedures.", "Change monitoring and alerting setup."]
  },
  "NIST-AI-MS-2.7-006": {
    title: "NIST AI 600-1 MS-2.7-006: Third-Party AI Supply Chain Security",
    summary: "Assess and manage security risks from third-party AI models, APIs, plugins, and data providers.",
    why: ["Third-party AI components inherit upstream risks.", "Supply chain attacks increasingly target AI distribution.", "Vendor security posture directly affects your AI risk."],
    goodLooksLike: ["Vendor security assessments for AI providers.", "Model provenance and integrity verification.", "Contractual security requirements for AI vendors."],
    evidence: ["Third-party AI vendor assessment reports.", "Model provenance verification records.", "AI vendor security contract clauses."]
  },
  "NIST-AI-MS-2.11-001": {
    title: "NIST AI 600-1 MS-2.11-001: Model Drift Detection",
    summary: "Detect and respond to model performance drift, data drift, and concept drift in deployed AI systems.",
    why: ["Model performance degrades over time.", "Data distribution shifts cause silent failures.", "Undetected drift leads to unreliable outputs."],
    goodLooksLike: ["Automated drift detection dashboards.", "Baseline performance metrics established.", "Alerting and retraining triggers defined."],
    evidence: ["Drift detection monitoring configurations.", "Performance baseline documentation.", "Drift alert and response records."]
  },
  "NIST-AI-MS-2.11-002": {
    title: "NIST AI 600-1 MS-2.11-002: AI Fairness Monitoring",
    summary: "Continuously monitor AI systems for bias, discrimination, and fairness violations in production.",
    why: ["Bias can emerge or shift post-deployment.", "Production data differs from training distributions.", "Regulatory compliance requires ongoing monitoring."],
    goodLooksLike: ["Fairness metrics tracked in production.", "Demographic parity and equal opportunity measured.", "Automated alerts for fairness threshold breaches."],
    evidence: ["Production fairness monitoring dashboards.", "Bias metric tracking reports.", "Fairness alert and remediation logs."]
  },
  "NIST-AI-MS-4.1-001": {
    title: "NIST AI 600-1 MS-4.1-001: AI Continuous Monitoring",
    summary: "Implement continuous monitoring for AI system health, security events, and operational anomalies.",
    why: ["AI systems face evolving threats post-deployment.", "Enables rapid detection of AI-specific incidents.", "Supports proactive security posture management."],
    goodLooksLike: ["Real-time AI monitoring dashboards.", "Security event correlation for AI systems.", "Automated anomaly detection and alerting."],
    evidence: ["AI monitoring architecture documentation.", "Security event dashboard screenshots.", "Alert rule configurations and escalation procedures."]
  },
  "NIST-AI-MS-4.1-002": {
    title: "NIST AI 600-1 MS-4.1-002: AI Logging & Audit Trail",
    summary: "Maintain comprehensive logging and audit trails for AI system interactions, decisions, and administrative actions.",
    why: ["Supports forensic investigation of AI incidents.", "Required for regulatory compliance and audits.", "Enables accountability for AI-driven decisions."],
    goodLooksLike: ["Comprehensive AI interaction logging.", "Tamper-resistant audit trails.", "Retention policies aligned with compliance requirements."],
    evidence: ["AI logging architecture documentation.", "Audit trail samples and retention configs.", "Log management and SIEM integration docs."]
  },
  "NIST-AI-MS-4.1-003": {
    title: "NIST AI 600-1 MS-4.1-003: AI Business Continuity",
    summary: "Ensure continuity of AI-dependent business processes through redundancy, failover, and recovery planning.",
    why: ["AI system outages disrupt critical business operations.", "Recovery planning prevents extended service degradation.", "Supports organizational resilience against AI failures."],
    goodLooksLike: ["AI-specific business continuity plans documented.", "Failover and rollback procedures tested.", "Recovery time objectives defined for AI systems."],
    evidence: ["AI business continuity plan.", "Failover test results and recovery metrics.", "AI system dependency mapping."]
  },
  "NIST-AI-MS-4.2-001": {
    title: "NIST AI 600-1 MS-4.2-001: AI Decision Explainability",
    summary: "Provide meaningful explanations for AI-driven decisions, especially those affecting individuals or high-stakes outcomes.",
    why: ["Individuals have rights to understand AI decisions.", "Explainability supports human override capability.", "Required by GDPR, EU AI Act, and other regulations."],
    goodLooksLike: ["Explanation methods selected per model type.", "User-facing explanations for automated decisions.", "Technical documentation of interpretability approach."],
    evidence: ["Explainability implementation documentation.", "Sample decision explanations.", "Interpretability technique evaluation reports."]
  },
  "NIST-AI-MS-4.2-002": {
    title: "NIST AI 600-1 MS-4.2-002: AI Stakeholder Reporting",
    summary: "Report AI system performance, risks, and governance outcomes to relevant stakeholders on a regular cadence.",
    why: ["Keeps leadership informed of AI risk posture.", "Supports regulatory reporting obligations.", "Enables data-driven governance decisions."],
    goodLooksLike: ["Regular AI performance and risk reports.", "Metrics dashboards accessible to stakeholders.", "Incident and near-miss reporting to governance bodies."],
    evidence: ["AI governance reports and dashboards.", "Stakeholder briefing materials.", "Incident reporting records."]
  },

  // Fallback
  "__DEFAULT__": {
    title: "Control Description",
    summary: "Industry control to reduce cybersecurity risk.",
    why: ["Clarifies expectations.", "Guides consistent implementation.", "Supports audits."],
    goodLooksLike: ["Documented approach.", "Measured adoption.", "Continuous improvement."],
    evidence: ["Policy/process excerpt.", "Implementation screenshot.", "Recent review ticket."]
  }
};